import { supabase } from './supabase';
import { t } from '../data/translations';

export interface AuthUser {
  name: string;
  secretKey: string;
  loginTime: Date;
}

export interface AuthResponse {
  success: boolean;
  user?: AuthUser;
  error?: string;
}

class AuthService {
  private readonly STORAGE_KEY = 'vlad30_auth';

  async login(name: string, secretKey: string): Promise<AuthResponse> {
    try {
      // Проверяем ключ в Supabase
      const { data: keyData, error: keyError } = await supabase
        .from('keys')
        .select('*')
        .eq('key', secretKey)
        .single();

      if (keyError || !keyData) {
        return {
          success: false,
          error: t('auth.invalidKey')
        };
      }

      // Убираем проверку на использование ключа - теперь ключ можно переиспользовать
      // Логируем использование ключа для статистики, но не блокируем повторное использование
      const { error: updateError } = await supabase
        .from('keys')
        .update({
          used_at: new Date().toISOString(),
          used_by: name
        })
        .eq('key', secretKey);

      if (updateError) {
        console.error('Error updating key:', updateError);
        // Не возвращаем ошибку, если не получилось обновить логи
      }

      // Создаем пользователя
      const user: AuthUser = {
        name: name.trim(),
        secretKey,
        loginTime: new Date()
      };

      // Сохраняем в localStorage
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(user));

      return {
        success: true,
        user
      };

    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: t('auth.loginFailed')
      };
    }
  }

  logout(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }

  checkExistingSession(): AuthUser | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const user = JSON.parse(stored);
        if (user.name && user.secretKey && user.loginTime) {
          return {
            ...user,
            loginTime: new Date(user.loginTime)
          };
        }
      } catch (error) {
        console.error('Error parsing stored auth data:', error);
      }
    }
    return null;
  }

  getCurrentUser(): AuthUser | null {
    return this.checkExistingSession();
  }

  // Метод для проверки валидности ключа без входа
  async validateKey(secretKey: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('keys')
        .select('key')
        .eq('key', secretKey)
        .single();

      return !error && data;
    } catch (error) {
      console.error('Key validation error:', error);
      return false;
    }
  }
}

export const authService = new AuthService(); 